import React, { useState, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

import Sidebar      from './components/Sidebar';
import Header       from './components/Header';

import Login             from './pages/Login';
import Dashboard         from './pages/Dashboard';
import UserManagement    from './pages/UserManagement';
import RoleManagement    from './pages/RoleManagement';
import AdminManagement   from './pages/AdminManagement';
import Reports           from './pages/Reports';
import Settings          from './pages/Settings';
import Profile           from './pages/Profile';
import Notifications     from './pages/Notifications';
import ContentManagement from './pages/ContentManagement';
import ActivityLogs      from './pages/ActivityLogs';
import Payments          from './pages/Payments';
import Transactions      from './pages/Transactions';
import CronJobs          from './pages/CronJobs';
import Battles           from './pages/Battles';
import Withdrawals       from './pages/Withdrawals';
import WalkSessions      from './pages/WalkSessions';
import Challenges        from './pages/Challenges';
import Referrals         from './pages/Referrals';
import Leaderboard       from './pages/Leaderboard';
import Rewards           from './pages/Rewards';
import AppConfig         from './pages/AppConfig';

// ─── In-memory activity log context (tracks admin actions in this session) ─────
export const ActivityContext = createContext(null);

export const useActivity = () => useContext(ActivityContext);

// ─── Protected Route ──────────────────────────────────────────────────────────
const ProtectedRoute = ({ children, permission }) => {
  const { isAuthenticated, hasPermission } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (permission && !hasPermission(permission)) return <Navigate to="/dashboard" replace />;
  return children;
};

// ─── App Shell ────────────────────────────────────────────────────────────────
const AppShell = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  return (
    <div className="app-layout">
      <Sidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
      />
      <div className={`main-content ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <Header
          onToggleSidebar={() => setSidebarCollapsed(p => !p)}
          onMobileMenu={() => setMobileSidebarOpen(p => !p)}
        />
        <main className="page-wrapper">
          <Routes>
            <Route path="/dashboard"     element={<ProtectedRoute permission="dashboard"><Dashboard /></ProtectedRoute>} />
            <Route path="/users"         element={<ProtectedRoute permission="users.view"><UserManagement /></ProtectedRoute>} />
            <Route path="/roles"         element={<ProtectedRoute permission="roles.view"><RoleManagement /></ProtectedRoute>} />
            <Route path="/admins"        element={<ProtectedRoute permission="admins"><AdminManagement /></ProtectedRoute>} />
            <Route path="/reports"       element={<ProtectedRoute permission="reports"><Reports /></ProtectedRoute>} />
            <Route path="/content"       element={<ProtectedRoute permission="content"><ContentManagement /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute permission="notifications"><Notifications /></ProtectedRoute>} />
            <Route path="/activity"      element={<ProtectedRoute permission="activity_logs"><ActivityLogs /></ProtectedRoute>} />
            <Route path="/battles"       element={<ProtectedRoute permission="battles"><Battles /></ProtectedRoute>} />
            <Route path="/payments"      element={<ProtectedRoute permission="reports"><Payments /></ProtectedRoute>} />
            <Route path="/transactions"  element={<ProtectedRoute permission="reports"><Transactions /></ProtectedRoute>} />
            <Route path="/cron-jobs"     element={<ProtectedRoute permission="settings"><CronJobs /></ProtectedRoute>} />
            <Route path="/withdrawals"   element={<ProtectedRoute permission="reports"><Withdrawals /></ProtectedRoute>} />
            <Route path="/walk-sessions" element={<ProtectedRoute permission="battles"><WalkSessions /></ProtectedRoute>} />
            <Route path="/challenges"    element={<ProtectedRoute permission="battles"><Challenges /></ProtectedRoute>} />
            <Route path="/referrals"     element={<ProtectedRoute permission="users.view"><Referrals /></ProtectedRoute>} />
            <Route path="/leaderboard"   element={<ProtectedRoute permission="dashboard"><Leaderboard /></ProtectedRoute>} />
            <Route path="/rewards"       element={<ProtectedRoute permission="battles"><Rewards /></ProtectedRoute>} />
            <Route path="/app-config"    element={<ProtectedRoute permission="settings"><AppConfig /></ProtectedRoute>} />
            <Route path="/settings"      element={<ProtectedRoute permission="settings"><Settings /></ProtectedRoute>} />
            <Route path="/profile"       element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="*"             element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// ─── Root ─────────────────────────────────────────────────────────────────────
const AppContent = () => {
  const { isAuthenticated, user } = useAuth();
  const [logs, setLogs] = useState([]);

  const addLog = useCallback((action, target, type = 'update') => {
    setLogs(prev => [{
      id: Date.now(),
      admin: user?.name || 'Unknown',
      action, target, type,
      timestamp: new Date().toISOString(),
    }, ...prev].slice(0, 200)); // keep last 200
  }, [user]);

  return (
    <ActivityContext.Provider value={{ logs, addLog }}>
      <Routes>
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/*"     element={isAuthenticated ? <AppShell /> : <Navigate to="/login" replace />} />
      </Routes>
    </ActivityContext.Provider>
  );
};

const App = () => (
  <BrowserRouter>
    <ThemeProvider>
    <AuthProvider>
      <AppContent />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1e293b',
            color: '#f1f5f9',
            border: '1px solid #2d3748',
            borderRadius: '10px',
            fontSize: '0.875rem',
          },
          success: { iconTheme: { primary: '#22c55e', secondary: '#1e293b' } },
          error:   { iconTheme: { primary: '#ef4444', secondary: '#1e293b' } },
        }}
      />
    </AuthProvider>
    </ThemeProvider>
  </BrowserRouter>
);

export default App;
